import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  User,
  Building2,
  Calendar,
  Target,
  Edit,
  CheckCircle2,
  XCircle,
  Ban,
  Undo2,
  Trash2,
  UserPlus2,
  ShieldCheck,
  MessageSquarePlus,
  FileText,
} from 'lucide-react';
import { Skeleton } from '../../../shared/components/Skeleton';
import { Alert } from '../../../shared/components/Alert';
import { listarClientes, listarContatos } from '../../clientes/api';
import { listarUsuarios } from '../../usuarios/api';
import { useEtapas, useOportunidade, useOrigens, useTipos } from '../hooks';
import {
  ConfidencialBadge,
  OportunidadePrioridadeBadge,
  OportunidadeStatusBadge,
  StalledBadge,
} from '../components/OportunidadeBadges';
import {
  CancelOpportunityModal,
  LoseOpportunityModal,
  MarginApprovalModal,
  ReactivateOpportunityModal,
  SuspendOpportunityModal,
  TransferResponsibleModal,
  WinOpportunityModal,
} from '../components/OpportunityActionModals';
import { InteractionModal } from '../components/InteractionModal';
import { ProposalModal } from '../components/ProposalModal';
import { OpportunityInteractionsList } from '../components/OpportunityInteractionsList';
import { OpportunityTimeline } from '../components/OpportunityTimeline';
import { OpportunityParticipantsSection } from '../components/OpportunityParticipantsSection';
import { OpportunityCompetitorsSection } from '../components/OpportunityCompetitorsSection';
import { OpportunityAttachmentsSection } from '../components/OpportunityAttachmentsSection';
import {
  formatCurrencyBRL,
  formatDateBR,
  formatDateTimeBR,
  formatPercent,
} from '../utils/format';

type ModalId =
  | 'win'
  | 'lose'
  | 'suspend'
  | 'reactivate'
  | 'cancel'
  | 'transfer'
  | 'margin'
  | 'interaction'
  | 'proposal';

/**
 * Detalhe de uma Oportunidade — Fatia 9 é read-only. Edição, movimento
 * de etapa, desfechos, interações, participantes, concorrentes e
 * anexos ficam para as fatias 10/11 do frontend.
 */
export default function OportunidadeDetalhePage(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: opp, isLoading, isError, error } = useOportunidade(id);
  const { data: stages } = useEtapas(true);
  const { data: origens } = useOrigens();
  const { data: tipos } = useTipos();
  const [openModal, setOpenModal] = useState<ModalId | null>(null);

  // Lookups por ID → label. Trazem os catálogos completos numa request
  // e ficam em cache do React Query (staleTime 60s). Cliente/usuários
  // limitados a 200 registros — se catálogos crescerem, migrar para
  // fetch pontual por ID.
  const { data: clientesResp } = useQuery({
    queryKey: ['picker', 'clientes'],
    queryFn: () => listarClientes({ limit: 200 }),
    staleTime: 60 * 1000,
  });
  const { data: usuariosResp } = useQuery({
    queryKey: ['picker', 'usuarios'],
    queryFn: () => listarUsuarios({ limit: 200 }),
    staleTime: 60 * 1000,
  });
  const { data: contatosResp } = useQuery({
    queryKey: ['picker', 'contatos', opp?.clientId],
    queryFn: () => listarContatos(opp!.clientId),
    enabled: !!opp?.clientId,
    staleTime: 30 * 1000,
  });

  const stageLabel = useMemo(() => {
    if (!opp || !stages) return null;
    return stages.find((s) => s.id === opp.stageId)?.label ?? null;
  }, [opp, stages]);

  const clienteInfo = useMemo(() => {
    if (!opp) return null;
    return (clientesResp?.data ?? []).find((c) => c.id === opp.clientId) ?? null;
  }, [opp, clientesResp]);

  const responsavelInfo = useMemo(() => {
    if (!opp) return null;
    return (usuariosResp?.data ?? []).find((u) => u.id === opp.responsavelId) ?? null;
  }, [opp, usuariosResp]);

  const contatoInfo = useMemo(() => {
    if (!opp) return null;
    return (contatosResp?.data ?? []).find((c) => c.id === opp.contatoPrincipalId) ?? null;
  }, [opp, contatosResp]);

  const origemLabel = useMemo(
    () => origens?.find((o) => o.id === opp?.origemId)?.label ?? null,
    [opp, origens],
  );
  const tipoLabel = useMemo(
    () => tipos?.find((t) => t.id === opp?.tipoId)?.label ?? null,
    [opp, tipos],
  );

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-2/3 mb-4" />
        <Skeleton className="h-4 w-1/2 mb-8" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (isError || !opp) {
    return (
      <div className="p-6 space-y-6">
        <button
          onClick={() => navigate('/oportunidades')}
          className="mb-4 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
        >
          <ArrowLeft className="h-4 w-4" /> Voltar para a lista
        </button>
        <Alert variant="error">
          {isError
            ? `Erro ao carregar oportunidade: ${(error as Error)?.message ?? 'Falha desconhecida.'}`
            : 'Oportunidade não encontrada ou sem permissão de visualização.'}
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <button
        onClick={() => navigate('/oportunidades')}
        className="mb-4 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
      >
        <ArrowLeft className="h-4 w-4" /> Voltar para a lista
      </button>

      {/* Header */}
      <div className="mb-6 flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm text-gray-600">
              {opp.codigo ?? 'RASCUNHO'}
            </span>
            <OportunidadeStatusBadge status={opp.status} />
            <OportunidadePrioridadeBadge prioridade={opp.prioridade} />
            {opp.isConfidential && <ConfidencialBadge />}
            {opp.isStalled && <StalledBadge />}
          </div>
          {!opp.isDraft && opp.status === 'aberta' && (
            <button
              onClick={() => navigate(`/oportunidades/${opp.id}/editar`)}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              <Edit className="h-4 w-4" />
              Editar
            </button>
          )}
        </div>
        <h1 className="text-2xl font-bold text-gray-900">{opp.nome}</h1>
        <p className="text-sm text-gray-600">
          {opp.produtoServico}
          {stageLabel && (
            <>
              {' • '}
              <span className="font-medium">Etapa: {stageLabel}</span>
            </>
          )}
        </p>
      </div>

      {/* Ciclo de vida — segue o padrão do ClienteDetalhePage: ações
          vivem dentro da própria section (não flutuam num box separado).
          Cada botão pode falhar por permissão no backend; o guard de
          carteira devolve 403 se executivo tentar operar oportunidade
          alheia (§Perfis e Permissões, Fatia 8). */}
      {!opp.isDraft && (
        <Section title="Ciclo de vida">
          <div className="flex flex-wrap gap-2">
            {opp.status === 'aberta' && (
              <>
                <ActionButton
                  icon={<CheckCircle2 className="h-4 w-4" />}
                  variant="success"
                  onClick={() => setOpenModal('win')}
                >
                  Marcar como Ganha
                </ActionButton>
                <ActionButton
                  icon={<XCircle className="h-4 w-4" />}
                  variant="danger"
                  onClick={() => setOpenModal('lose')}
                >
                  Marcar como Perdida
                </ActionButton>
                <ActionButton
                  icon={<Ban className="h-4 w-4" />}
                  variant="warning"
                  onClick={() => setOpenModal('suspend')}
                >
                  Suspender
                </ActionButton>
                <ActionButton
                  icon={<Trash2 className="h-4 w-4" />}
                  variant="danger"
                  onClick={() => setOpenModal('cancel')}
                >
                  Cancelar
                </ActionButton>
                <ActionButton
                  icon={<UserPlus2 className="h-4 w-4" />}
                  variant="neutral"
                  onClick={() => setOpenModal('transfer')}
                >
                  Transferir responsável
                </ActionButton>
                {opp.marginBelowThreshold && (
                  <ActionButton
                    icon={<ShieldCheck className="h-4 w-4" />}
                    variant="warning"
                    onClick={() => setOpenModal('margin')}
                  >
                    Aprovar margem
                  </ActionButton>
                )}
                <ActionButton
                  icon={<FileText className="h-4 w-4" />}
                  variant="primary"
                  onClick={() => setOpenModal('proposal')}
                >
                  Gerar proposta
                </ActionButton>
              </>
            )}
            {opp.status === 'suspensa' && (
              <ActionButton
                icon={<Undo2 className="h-4 w-4" />}
                variant="primary"
                onClick={() => setOpenModal('reactivate')}
              >
                Reativar
              </ActionButton>
            )}
            {opp.status !== 'aberta' && opp.status !== 'suspensa' && (
              <p className="text-sm text-gray-500">
                Oportunidade em desfecho terminal ({opp.status}). Nenhuma ação disponível.
              </p>
            )}
          </div>
        </Section>
      )}

      {/* Cards de indicadores da oportunidade */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <IndicatorCard
          icon={<Target className="h-4 w-4" />}
          label="Valor Estimado"
          value={formatCurrencyBRL(opp.valorEstimado)}
        />
        <IndicatorCard
          icon={<Target className="h-4 w-4" />}
          label="Valor Ponderado"
          value={formatCurrencyBRL(opp.valorPonderado)}
          hint={
            opp.probabilidade != null
              ? `${opp.probabilidade}% de probabilidade`
              : undefined
          }
        />
        <IndicatorCard
          icon={<Target className="h-4 w-4" />}
          label="Margem"
          value={formatPercent(opp.margemPercentual)}
          hint={
            opp.marginBelowThreshold
              ? 'Abaixo do limite mínimo'
              : undefined
          }
          alert={opp.marginBelowThreshold}
        />
        <IndicatorCard
          icon={<Calendar className="h-4 w-4" />}
          label="Fechamento Previsto"
          value={formatDateBR(opp.expectedClosingDate)}
          hint={opp.nextActionOverdue ? 'Próxima ação vencida' : undefined}
          alert={opp.nextActionOverdue}
        />
      </div>

      {/* Blocos read-only. Cada bloco vira uma aba nas Fatias posteriores. */}
      <Section title="Dados Gerais">
        <DataGrid>
          <Field icon={<User />} label="Responsável Comercial">
            {responsavelInfo ? (
              <div>
                <div>{responsavelInfo.name}</div>
                <div className="text-xs text-gray-500">{responsavelInfo.email}</div>
              </div>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </Field>
          <Field icon={<Building2 />} label="Cliente">
            {clienteInfo ? (
              <Link
                to={`/clientes/${clienteInfo.id}`}
                className="text-blue-600 hover:underline"
              >
                {clienteInfo.razaoSocial}
                <span className="ml-1 text-xs text-gray-500">({clienteInfo.code})</span>
              </Link>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </Field>
          <Field label="Contato Principal">
            {contatoInfo ? (
              <div>
                <div>{contatoInfo.nome}</div>
                <div className="text-xs text-gray-500">
                  {contatoInfo.cargo ? `${contatoInfo.cargo} • ` : ''}
                  {contatoInfo.email}
                </div>
              </div>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </Field>
          <Field label="Unidade / Filial">
            {/* Master data de unidade ainda não existe (design.md,
                Open Questions §2). Quando entrar spec futura, resolver
                nome via lookup igual às demais. */}
            {opp.unidadeId ? (
              <span className="text-gray-600">Vinculada</span>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </Field>
          <Field label="Origem">
            {origemLabel ?? <span className="text-gray-400">—</span>}
          </Field>
          <Field label="Tipo">
            {tipoLabel ?? <span className="text-gray-400">—</span>}
          </Field>
        </DataGrid>
      </Section>

      <Section title="Financeiro">
        <DataGrid>
          <Field label="Valor Estimado">{formatCurrencyBRL(opp.valorEstimado)}</Field>
          <Field label="Custo Estimado">{formatCurrencyBRL(opp.custoEstimado)}</Field>
          <Field label="Moeda">{opp.moeda}</Field>
          <Field label="Probabilidade">
            {opp.probabilidade != null ? `${opp.probabilidade}%` : '—'}
          </Field>
          <Field label="Valor Ponderado">{formatCurrencyBRL(opp.valorPonderado)}</Field>
          <Field label="Margem Valor">{formatCurrencyBRL(opp.margemValor)}</Field>
          <Field label="Margem %">{formatPercent(opp.margemPercentual)}</Field>
        </DataGrid>
      </Section>

      <Section title="Necessidade e Escopo">
        <div className="space-y-3 text-sm">
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Necessidade
            </div>
            <p className="mt-1 whitespace-pre-wrap text-gray-800">{opp.necessidade}</p>
          </div>
          {opp.escopo && (
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Escopo
              </div>
              <p className="mt-1 whitespace-pre-wrap text-gray-800">{opp.escopo}</p>
            </div>
          )}
          {opp.observacoes && (
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Observações
              </div>
              <p className="mt-1 whitespace-pre-wrap text-gray-800">{opp.observacoes}</p>
            </div>
          )}
        </div>
      </Section>

      <Section title="Próxima Ação">
        <DataGrid>
          <Field label="Data">{formatDateTimeBR(opp.nextActionDate)}</Field>
          <Field label="Descrição">{opp.nextActionDescription ?? '—'}</Field>
          <Field label="Vencida?">
            {opp.nextActionOverdue ? (
              <span className="text-red-700">Sim — pendente</span>
            ) : (
              'Não'
            )}
          </Field>
        </DataGrid>
      </Section>

      <Section title="Auditoria">
        <DataGrid>
          <Field label="Criado em">{formatDateTimeBR(opp.createdAt)}</Field>
          <Field label="Atualizado em">{formatDateTimeBR(opp.updatedAt)}</Field>
          <Field label="Criado por">
            {resolveUserName(opp.createdBy, usuariosResp?.data ?? [])}
          </Field>
          <Field label="Atualizado por">
            {resolveUserName(opp.updatedBy, usuariosResp?.data ?? [])}
          </Field>
        </DataGrid>
      </Section>

      {/* Interações (§Interações Comerciais). Backend reseta is_stalled
          ao criar — a UI reflete via invalidação. */}
      {!opp.isDraft && (
        <section className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
              Interações
            </h2>
            {opp.status === 'aberta' && (
              <button
                onClick={() => setOpenModal('interaction')}
                className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
              >
                <MessageSquarePlus className="h-3 w-3" />
                Registrar interação
              </button>
            )}
          </div>
          <OpportunityInteractionsList opportunityId={opp.id} />
        </section>
      )}

      {!opp.isDraft && (
        <OpportunityParticipantsSection
          opportunityId={opp.id}
          responsavelId={opp.responsavelId}
          canEdit={opp.status === 'aberta'}
        />
      )}

      {!opp.isDraft && (
        <OpportunityCompetitorsSection
          opportunityId={opp.id}
          canEdit={opp.status === 'aberta'}
        />
      )}

      {!opp.isDraft && (
        <OpportunityAttachmentsSection
          opportunityId={opp.id}
          canEdit={opp.status !== 'ganha' && opp.status !== 'perdida' && opp.status !== 'cancelada'}
        />
      )}

      {/* Histórico completo — trilha de auditoria (§Auditoria e Histórico) */}
      {!opp.isDraft && (
        <section className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-700">
            Histórico
          </h2>
          <OpportunityTimeline opportunityId={opp.id} />
        </section>
      )}

      <p className="mt-8 text-xs italic text-gray-500">
        Movimento de etapa entre as visões Lista/Kanban — arraste no Kanban.
      </p>

      {/* Modais de ação — renderizados condicionalmente para não montar
          o form/queries até que sejam abertos. */}
      <WinOpportunityModal opp={opp} isOpen={openModal === 'win'} onClose={() => setOpenModal(null)} />
      <LoseOpportunityModal opp={opp} isOpen={openModal === 'lose'} onClose={() => setOpenModal(null)} />
      <SuspendOpportunityModal opp={opp} isOpen={openModal === 'suspend'} onClose={() => setOpenModal(null)} />
      <ReactivateOpportunityModal opp={opp} isOpen={openModal === 'reactivate'} onClose={() => setOpenModal(null)} />
      <CancelOpportunityModal opp={opp} isOpen={openModal === 'cancel'} onClose={() => setOpenModal(null)} />
      <TransferResponsibleModal opp={opp} isOpen={openModal === 'transfer'} onClose={() => setOpenModal(null)} />
      <MarginApprovalModal opp={opp} isOpen={openModal === 'margin'} onClose={() => setOpenModal(null)} />
      <InteractionModal opp={opp} isOpen={openModal === 'interaction'} onClose={() => setOpenModal(null)} />
      <ProposalModal opp={opp} isOpen={openModal === 'proposal'} onClose={() => setOpenModal(null)} />
    </div>
  );
}

type ActionVariant = 'primary' | 'success' | 'danger' | 'warning' | 'neutral';

// Alinhado ao ClienteSituacaoActions: todas as variantes são solid
// (sem outline branco). success/warning têm o mesmo tratamento de
// contraste que o resto (label branca sobre fill colorido).
const ACTION_STYLES: Record<ActionVariant, string> = {
  primary: 'bg-blue-600 text-white hover:bg-blue-700',
  success: 'bg-green-600 text-white hover:bg-green-700',
  danger: 'bg-red-600 text-white hover:bg-red-700',
  warning: 'bg-yellow-500 text-white hover:bg-yellow-600',
  neutral: 'bg-gray-600 text-white hover:bg-gray-700',
};

const ActionButton: React.FC<
  React.PropsWithChildren<{ icon?: React.ReactNode; variant?: ActionVariant; onClick: () => void }>
> = ({ children, icon, variant = 'primary', onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 ${ACTION_STYLES[variant]}`}
  >
    {icon}
    {children}
  </button>
);

const Section: React.FC<React.PropsWithChildren<{ title: string }>> = ({ title, children }) => (
  <section className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
    <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-700">
      {title}
    </h2>
    {children}
  </section>
);

const DataGrid: React.FC<React.PropsWithChildren> = ({ children }) => (
  <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
    {children}
  </dl>
);

interface FieldProps {
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}
const Field: React.FC<FieldProps> = ({ label, icon, children }) => (
  <div>
    <dt className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-gray-500">
      {icon && <span className="text-gray-400" aria-hidden>{icon}</span>}
      {label}
    </dt>
    <dd className="mt-0.5 text-sm text-gray-800">{children}</dd>
  </div>
);

interface IndicatorCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  alert?: boolean;
}
function resolveUserName(
  userId: string | null | undefined,
  users: { id: string; name: string }[],
): React.ReactNode {
  if (!userId) return <span className="text-gray-400">—</span>;
  const found = users.find((u) => u.id === userId);
  return found?.name ?? <span className="text-gray-400">—</span>;
}

const IndicatorCard: React.FC<IndicatorCardProps> = ({ icon, label, value, hint, alert }) => (
  <div
    className={`rounded-lg border p-3 shadow-sm ${
      alert ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'
    }`}
  >
    <div className="flex items-center gap-1 text-xs text-gray-500">
      <span aria-hidden>{icon}</span>
      {label}
    </div>
    <div className={`mt-1 text-lg font-semibold tabular-nums ${alert ? 'text-amber-800' : 'text-gray-900'}`}>
      {value}
    </div>
    {hint && (
      <div className={`text-xs ${alert ? 'text-amber-700' : 'text-gray-500'}`}>{hint}</div>
    )}
  </div>
);
